import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { getEffectiveLocationHash } from "../../navigation/navPathUtils";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../healthLayout";
import {
    healthChildPageMetaLink,
    healthClinicalAgentsActiveWrap,
    healthClinicalAgentsPlatformLinkWrap,
    healthClinicalAgentsShell,
    healthFocusVisible,
} from "../healthVisualStyles";
import { HealthClinicalAgentTabSelector } from "../aimmaDoctor/HealthClinicalAgentTabSelector";
import { healthLocalePath } from "../aimmaDoctor/healthLocalePath";
import { getAimmaPatientModulePreviewSrc } from "./healthAimmaPatientPreviewAssets";
import { HealthPatientModuleDetailPanel } from "./HealthPatientModuleDetailPanel";

interface HealthPatientModulesSectionProps {
    data: DataKeys;
    clinicalInputsLabel: string;
    outputsLabel: string;
    patientCareNoteLabel: string;
    workflowBenefitLabel: string;
}

export function HealthPatientModulesSection({
    data,
    clinicalInputsLabel,
    outputsLabel,
    patientCareNoteLabel,
    workflowBenefitLabel,
}: HealthPatientModulesSectionProps) {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;
    const blocks = (data.blocks as DataKeys[]) ?? [];
    const platformLink = data.platformLink as DataKeys | undefined;
    const [activeIndex, setActiveIndex] = useState(0);
    const prevIndexRef = useRef(0);
    const panelIdPrefix = "modules";

    const selectModule = (index: number) => {
        prevIndexRef.current = activeIndex;
        setActiveIndex(index);
    };

    useEffect(() => {
        const hash = getEffectiveLocationHash(location.hash);

        if (!hash || hash === "#modules") {
            setActiveIndex((current) => {
                if (current === 0) return current;
                prevIndexRef.current = current;
                return 0;
            });
            return;
        }

        if (!hash.startsWith("#modules-")) return;

        const productId = hash.replace("#modules-", "");
        const index = blocks.findIndex((block) => block.id === productId);
        if (index >= 0) {
            setActiveIndex((current) => {
                prevIndexRef.current = current;
                return index;
            });
        }
    }, [blocks, location.hash, lang]);

    const activeBlock = blocks[activeIndex];
    const activeProductId = activeBlock?.id as string | undefined;
    const transitionDirection =
        activeIndex >= prevIndexRef.current ? 1 : -1;
    const previewSrc = getAimmaPatientModulePreviewSrc(activeProductId);
    const previewAlt = activeBlock
        ? getLocalized(
              (activeBlock.visual as DataKeys)?.alt as LocalizedString,
              hLang
          )
        : "Patient product interface preview";
    const summaryLabel = getLocalized(
        data.summaryLabel as LocalizedString,
        hLang
    );
    const tabListAriaLabel = getLocalized(
        data.tabListAriaLabel as LocalizedString,
        hLang
    );
    const tabPreviousAriaLabel = getLocalized(
        data.tabPreviousAriaLabel as LocalizedString,
        hLang
    );
    const tabNextAriaLabel = getLocalized(
        data.tabNextAriaLabel as LocalizedString,
        hLang
    );

    return (
        <HealthSection
            id="modules"
            ariaLabelledBy="modules-title"
            compact
            className="scroll-mt-24"
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="modules-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div
                className={`${healthAimmaDoctorSectionContentOffset} ${healthClinicalAgentsShell}`}
            >
                <HealthClinicalAgentTabSelector
                    blocks={blocks}
                    activeIndex={activeIndex}
                    onSelect={selectModule}
                    lang={hLang}
                    panelIdPrefix={panelIdPrefix}
                    tabListAriaLabel={tabListAriaLabel}
                    tabPreviousAriaLabel={tabPreviousAriaLabel}
                    tabNextAriaLabel={tabNextAriaLabel}
                />

                <div className={healthClinicalAgentsActiveWrap}>
                    {activeBlock ? (
                        <HealthPatientModuleDetailPanel
                            block={activeBlock}
                            lang={hLang}
                            transitionDirection={transitionDirection}
                            clinicalInputsLabel={clinicalInputsLabel}
                            outputsLabel={outputsLabel}
                            patientCareNoteLabel={patientCareNoteLabel}
                            workflowBenefitLabel={workflowBenefitLabel}
                            summaryLabel={summaryLabel}
                            panelId={`${panelIdPrefix}-${activeProductId}`}
                            tabId={`${panelIdPrefix}-${activeProductId}-tab`}
                            previewSrc={previewSrc}
                            previewAlt={previewAlt}
                        />
                    ) : null}
                </div>

                {platformLink?.href ? (
                    <div className={healthClinicalAgentsPlatformLinkWrap}>
                        <Link
                            to={healthLocalePath(
                                platformLink.href as string,
                                hLang
                            )}
                            className={`${healthChildPageMetaLink} ${healthFocusVisible}`}
                        >
                            {getLocalized(
                                platformLink.label as LocalizedString,
                                hLang
                            )}
                            <span aria-hidden="true"> →</span>
                        </Link>
                    </div>
                ) : null}
            </div>
        </HealthSection>
    );
}
